import React from "react";

const palette = {
  ink: "#49372C",
  muted: "#79695D",
  cream: "#FBF7F0",
  paper: "#FFFDF9",
  border: "#E8DCCB",
  gold: "#B78A42",
  goldDark: "#8A642B",
  focus: "#6C4B25",
};

const buttonBase = {
  minHeight: 46,
  borderRadius: 8,
  font: "inherit",
  fontWeight: 650,
  cursor: "pointer",
  transition: "background-color 140ms ease, border-color 140ms ease, transform 140ms ease",
};

function getScopeName(stores, currentScope) {
  if (currentScope === "all") return "Todas as lojas";
  const store = stores.find((item) => String(item.id) === String(currentScope));
  return store ? store.name : "Escopo não identificado";
}

export default function AdminStoreEntry({
  stores = [],
  onSelect,
  onLogout,
  onOpenSafetyAlerts,
  currentScope = null,
  compact = false,
}) {
  if (compact) {
    return (
      <section
        aria-label="Escopo de lojas"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          color: palette.ink,
          fontFamily: "inherit",
        }}
      >
        <div aria-live="polite">
          <span style={{ color: palette.muted, fontSize: 12, display: "block" }}>
            Escopo atual
          </span>
          <strong style={{ fontSize: 14 }}>{getScopeName(stores, currentScope)}</strong>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
          <span style={{ color: palette.muted, fontSize: 13 }}>Alterar</span>
          <select
            aria-label="Trocar loja"
            defaultValue=""
            onChange={(event) => {
              const value = event.target.value;
              if (value === "all") onSelect?.("all");
              else if (value !== "") onSelect?.(Number(value));
              event.target.value = "";
            }}
            style={{
              minHeight: 40,
              maxWidth: 230,
              padding: "8px 34px 8px 11px",
              border: `1px solid ${palette.border}`,
              borderRadius: 7,
              color: palette.ink,
              background: palette.paper,
              font: "inherit",
              cursor: "pointer",
            }}
          >
            <option value="" disabled>Trocar loja</option>
            {stores.map((store) => (
              <option key={store.id} value={String(store.id)}>{store.name}</option>
            ))}
            <option value="all">Todas as lojas</option>
          </select>
        </label>
      </section>
    );
  }

  return (
    <main
      style={{
        minHeight: "100dvh",
        boxSizing: "border-box",
        display: "grid",
        placeItems: "center",
        padding: "32px 18px",
        background: palette.cream,
        color: palette.ink,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <section
        aria-labelledby="admin-store-entry-title"
        style={{
          width: "min(100%, 470px)",
          boxSizing: "border-box",
          padding: "clamp(24px, 6vw, 38px)",
          background: palette.paper,
          border: `1px solid ${palette.border}`,
          borderRadius: 14,
          boxShadow: "0 14px 38px rgba(78, 54, 29, 0.08)",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 38,
            height: 3,
            marginBottom: 22,
            borderRadius: 3,
            background: palette.gold,
          }}
        />
        <h1
          id="admin-store-entry-title"
          style={{
            margin: 0,
            color: palette.ink,
            fontSize: "clamp(23px, 5vw, 29px)",
            letterSpacing: "-0.035em",
            lineHeight: 1.2,
          }}
        >
          Escolha a loja para entrar
        </h1>
        <p style={{ margin: "11px 0 24px", color: palette.muted, fontSize: 14, lineHeight: 1.6 }}>
          Ao escolher uma loja, o CRM carrega somente os dados dela e mantém suas permissões administrativas.
        </p>

        <div style={{ display: "grid", gap: 10 }}>
          {stores.map((store) => (
            <button
              key={store.id}
              type="button"
              onClick={() => onSelect?.(Number(store.id))}
              style={{
                ...buttonBase,
                width: "100%",
                padding: "11px 15px",
                border: `1px solid ${palette.border}`,
                background: palette.paper,
                color: palette.ink,
                textAlign: "left",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = palette.cream;
                event.currentTarget.style.borderColor = palette.gold;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = palette.paper;
                event.currentTarget.style.borderColor = palette.border;
              }}
              onFocus={(event) => {
                event.currentTarget.style.outline = `3px solid ${palette.gold}66`;
                event.currentTarget.style.outlineOffset = 2;
              }}
              onBlur={(event) => {
                event.currentTarget.style.outline = "none";
              }}
            >
              {store.name}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 18, padding: "13px 14px", borderRadius: 8, background: "#F7F0E5" }}>
          <button
            type="button"
            onClick={() => onSelect?.("all")}
            style={{
              ...buttonBase,
              minHeight: 40,
              padding: "8px 0",
              border: 0,
              background: "transparent",
              color: palette.goldDark,
              textAlign: "left",
            }}
            onFocus={(event) => { event.currentTarget.style.outline = `3px solid ${palette.gold}66`; }}
            onBlur={(event) => { event.currentTarget.style.outline = "none"; }}
          >
            Ver todas as lojas
          </button>
          <p style={{ margin: "2px 0 0", color: palette.muted, fontSize: 12, lineHeight: 1.5 }}>
            A visão geral reúne dados de todas as lojas e pode levar mais tempo para carregar.
          </p>
        </div>

        {onOpenSafetyAlerts && <button type="button" onClick={onOpenSafetyAlerts}
          style={{ ...buttonBase, width:"100%", minHeight:42, marginTop:20,
            border:`1px solid ${palette.border}`, background:palette.paper, color:palette.ink }}>
          Alertas do WhatsApp
        </button>}
        <button
          type="button"
          onClick={onLogout}
          style={{
            ...buttonBase,
            width: "100%",
            minHeight: 42,
            marginTop: 20,
            border: `1px solid ${palette.border}`,
            background: "transparent",
            color: palette.muted,
          }}
          onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = palette.cream; }}
          onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = "transparent"; }}
          onFocus={(event) => { event.currentTarget.style.outline = `3px solid ${palette.gold}66`; }}
          onBlur={(event) => { event.currentTarget.style.outline = "none"; }}
        >
          Sair
        </button>
      </section>
    </main>
  );
}
